import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { format } from 'date-fns/format';

import { FactionProfile } from '@wwe-portal/entities/faction.type';
import { percentFormatter, tryFormatThousands } from '@wwe-portal/utils/format';

export function useFactionDetails(
  t: ReturnType<typeof useTranslations>,
  profile?: FactionProfile
) {
  const factionName = useMemo(() => {
    return profile?.faction_name || profile?.alliance_name || '';
  }, [profile]);
  const activeMemberCount = useMemo(() => {
    return profile?.active_member_count ?? 0;
  }, [profile]);
  const factionTier = useMemo(() => {
    return profile?.faction_tier || 0;
  }, [profile]);
  const createdDate = useMemo(() => {
    if (profile && profile.faction_created_date) {
      return format(new Date(profile.faction_created_date), 'MM/dd/yyyy');
    } else {
      return '';
    }
  }, [profile]);
  const factionAge = useMemo(() => {
    const count = profile?.faction_age || 1;
    const label = count === 1 ? t('day') : t('days');
    return `${tryFormatThousands(count)} ${label}`;
  }, [profile]);

  const lastFeudRank = useMemo(() => {
    const rank = profile?.last_feud_rank ?? 0;
    if (rank > 0) {
      return `${rank}`;
    } else {
      return 'N/A';
    }
  }, [profile]);
  const totalRosterTalent = useMemo(() => {
    return tryFormatThousands(profile?.faction_total_roster_talent || 0);
  }, [profile]);
  const totalRosterSize = useMemo(() => {
    return tryFormatThousands(profile?.faction_total_roster_size || 0);
  }, [profile]);
  const battlesPerPlayer = useMemo(() => {
    const last30dBattles = profile?.last_30d_battles || 0;
    const activeMemberCount = profile?.active_member_count || 0;
    if (profile && activeMemberCount > 0) {
      return tryFormatThousands(Math.round(last30dBattles / activeMemberCount));
    } else {
      return `0`;
    }
  }, [profile]);

  const allTimeWins = useMemo(() => {
    return tryFormatThousands(profile?.all_time_wins ?? 0);
  }, [profile]);
  const last30dWins = useMemo(() => {
    return tryFormatThousands(profile?.last_30d_wins ?? 0);
  }, [profile]);

  const allTimeBattles = useMemo(() => {
    return tryFormatThousands(profile?.all_time_battles ?? 0);
  }, [profile]);
  const last30dBattles = useMemo(() => {
    return tryFormatThousands(profile?.last_30d_battles ?? 0);
  }, [profile]);

  const allTimeWinRate = useMemo(() => {
    return percentFormatter(profile?.all_time_win_rate ?? 0, 1);
  }, [profile]);
  const last30dWinRate = useMemo(() => {
    return percentFormatter(profile?.last_30d_win_rate ?? 0, 1);
  }, [profile]);

  return {
    factionName,
    activeMemberCount,
    createdDate,
    factionAge,
    factionTier,
    lastFeudRank,
    totalRosterTalent,
    totalRosterSize,
    battlesPerPlayer,
    allTimeWins,
    last30dWins,
    allTimeBattles,
    last30dBattles,
    allTimeWinRate,
    last30dWinRate,
  };
}
